<script lang="ts">
  import { fade } from 'svelte/transition'
  import { Check, Save, AlertCircle, Loader2, Cloud, CloudOff } from 'lucide-svelte'
  
  /**
   * SaveIndicator: Display save status and progress
   * Single Responsibility: Visual feedback for save operations
   */
  
  interface Props {
    status: 'idle' | 'saving' | 'saved' | 'error'
    lastSaved?: Date | null
    isDirty?: boolean
    message?: string
    class?: string
  }
  
  let {
    status = 'idle',
    lastSaved = null,
    isDirty = false,
    message = '',
    class: className = ''
  }: Props = $props()
  
  function formatLastSaved(date: Date | null): string {
    if (!date) return ''
    
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    
    if (seconds < 10) return '방금 저장됨'
    if (seconds < 60) return `${seconds}초 전 저장됨`
    if (minutes < 60) return `${minutes}분 전 저장됨`
    if (hours < 24) return `${hours}시간 전 저장됨`
    if (days === 1) return '어제 저장됨'
    if (days < 7) return `${days}일 전 저장됨`
    
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  let displayText = $derived(() => {
    if (message) return message
    
    switch (status) {
      case 'saving':
        return '저장 중...'
      case 'saved':
        return lastSaved ? formatLastSaved(lastSaved) : '저장됨'
      case 'error':
        return '저장 실패'
      default:
        if (isDirty) return '저장되지 않음'
        if (lastSaved) return formatLastSaved(lastSaved)
        return ''
    }
  })
  
  let statusIcon = $derived(() => {
    switch (status) {
      case 'saving':
        return Loader2
      case 'saved':
        return Check
      case 'error':
        return AlertCircle
      default:
        return isDirty ? Save : Cloud
    }
  })
  
  let statusClass = $derived(() => {
    switch (status) {
      case 'saving':
        return 'saving'
      case 'saved':
        return 'saved'
      case 'error':
        return 'error'
      default:
        return isDirty ? 'dirty' : 'idle'
    }
  })
  
  let showIndicator = $derived(
    status !== 'idle' || isDirty || (lastSaved && Date.now() - lastSaved.getTime() < 5000)
  )
</script>

{#if showIndicator}
  <div 
    class="save-indicator {statusClass} {className}"
    transition:fade={{ duration: 200 }}
  >
    <span class="indicator-icon">
      {#if status === 'saving'}
        {@const Icon = statusIcon}
        <Icon size={14} class="spin" />
      {:else}
        {@const Icon = statusIcon}
        <Icon size={14} />
      {/if}
    </span>
    
    {#if displayText()}
      <span class="indicator-text">
        {displayText()}
      </span>
    {/if}
    
    {#if isDirty}
      <span class="dirty-dot"></span>
    {/if}
  </div>
{/if}

<style>
  .save-indicator {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: var(--ui-font-size-xs);
    transition: all 0.2s ease;
    user-select: none;
  }
  
  .indicator-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .indicator-text {
    white-space: nowrap;
  }
  
  /* Status Styles */
  .save-indicator.idle {
    color: var(--text-tertiary);
    background: transparent;
  }
  
  .save-indicator.saving {
    color: var(--info);
    background: rgba(59, 130, 246, 0.1);
  }
  
  .save-indicator.saved {
    color: var(--success);
    background: rgba(34, 197, 94, 0.1);
  }
  
  .save-indicator.error {
    color: var(--error);
    background: rgba(239, 68, 68, 0.1);
  }
  
  .save-indicator.dirty {
    color: var(--warning);
    background: rgba(245, 158, 11, 0.1);
  }
  
  /* Dirty Indicator */
  .dirty-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--warning);
    animation: pulse 2s infinite;
  }
  
  /* Spinning Animation */
  :global(.save-indicator .spin) {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  
  /* Compact Mode */
  .save-indicator.compact {
    padding: 2px 6px;
    gap: 4px;
  }
  
  .save-indicator.compact .indicator-text {
    display: none;
  }
  
  /* Inline Mode */
  .save-indicator.inline {
    background: transparent !important;
    padding: 0;
  }
</style>